import HelpCircle from "@untitled-ui/icons-react/build/esm/HelpCircle";
import InfoCircle from "@untitled-ui/icons-react/build/esm/InfoCircle";
import TooltipSimple from "../../../common/tooltip/TooltipSimple";
import moment from "moment";
import { useContext, useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router";
import styled from "styled-components";
import { AppQueryClient } from "../../../../api/queryClient";
import AppContext from "../../../../AppContext";
import { APP_TITLE } from "../../../../config/constant";
import PopupContext from "../../../../PopupContext";
import { getColorFromStatus } from "../../../../utils/CardUtils";
import { formatDefaultDate } from "../../../../utils/Formatter";
import * as Library from "../../../../utils/Library";
import BtnLargeSolid from "../../../common/buttons/BtnLargeSolid";
import CheckboxVector from "../../../common/checkbox/CheckboxVector";
import DialogBoxComponent from "../../../common/dialogs/DialogBoxComponent";
import FormContainer from "../../../common/forms/FormContainer";
import FormField from "../../../common/forms/FormField";
import FormFieldDatePicker from "../../../common/forms/FormFieldDatePicker";
import FormFieldDateRangePicker from "../../../common/forms/FormFieldDateRangePicker";
import ListComponent, { StatusBadge } from "../../../common/lists/ListComponent";
import ContainerLoader from "../../../common/loaders/ContainerLoader";
import MainWindowHeader, { CustomSearch } from "../../../common/mainApp/mainWindowHeaders/MainWindowHeader";
import FullScreenOverlay from "../../../common/overlay/FullScreenOverlay";
import { DialogWithIcon } from "../POSExportPage";

// #region SCOPED CSS
const StyledPage = styled.div`
    &.my-defaults-seasons-page {
        display: flex;
        flex-direction: column;
    }

    .add-season-row {
        display: flex;
        align-items: flex-end;
        gap: 16px;
        width: 100%;
    }

    .add-season-fields {
        display: grid;
        grid-template-columns: 1fr auto 1fr 1fr;
        gap: 16px;
        flex: 1;
        min-width: 0;
    }

    .append-month-control {
        display: flex;
        align-items: start;
        gap: 8px;
        min-height: 37px;
        flex-shrink: 0;
        white-space: nowrap;
        align-self: start;
        padding-top: 32px;
    }

    .append-month-label {
        color: var(--text-icons-gray-100);
        font-size: 14px;
        font-weight: 400;
        top: -2px;
        position: relative;
    }

    .append-month-info {
        width: 16px;
        height: 16px;
        color: var(--text-icons-gray-200);
        flex-shrink: 0;
    }

    .add-season-button {
        width: 64px;
        height: 37px;
        flex-shrink: 0;
        align-self: start;
        margin-top: 21px;
    }

    @media (max-width: 1500px) {
        .add-season-row {
            flex-wrap: wrap;
        }

        .add-season-fields {
            grid-template-columns: repeat(2, minmax(0, 1fr));
            flex-basis: 100%;
        }
    }

    .rdt_Pagination {
        display: none !important;
    }

    .table-structure > div:nth-child(2) {
        height: 24px;
    }

    .delivery-window-field .Frame48RightContent,
    .end-of-lifecycle-field .Frame48RightContent {
        // remove this: border-left: 1px solid var(--backgrounds-lines-n-100);
        border-left: none !important;
    }
`;
// #endregion

export default function MyDefaultSeasonsPage() {
    document.title = `${APP_TITLE} - My Default Seasons`;

    // #region VARS
    const { isLoadingApp } = useContext(AppContext);
    const { MainLayoutProps, layoutType } = useOutletContext();
    const { setGenericConfirmationPopup } = useContext(PopupContext);
    const { setHasUnsavedChanges } = useContext(AppContext);
    const onExportClickedRef = useRef(null);

    const props = {
        layoutType: layoutType,
        setControlBarLeftBtns: MainLayoutProps?.setControlBarLeftBtns,
        setControlBarCenterBtns: MainLayoutProps?.setControlBarCenterBtns,
        setControlBarRightBtns: MainLayoutProps?.setControlBarRightBtns,
        setFilterHeaderProperties: MainLayoutProps?.setFilterHeaderProperties,
        setRefreshUserData: MainLayoutProps?.setRefreshUserData
    };
    // #endregion

    // #region STATES
    const [allRetailBrandsList, setAllRetailBrandsList] = useState([]);
    const [allPOSList, setAllPOSList] = useState([]);
    const [selectedRetailBrand, setSelectedRetailBrand] = useState(null);
    const [filteredPOS, setFilteredPOS] = useState(undefined);
    const [requestsEnabled, setRequestsEnabled] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [exportButtonEnabled, setExportButtonEnabled] = useState(false);

    const filters = {
        filterRetailBrandIDs: selectedRetailBrand ? [selectedRetailBrand?.value] : [],
        filterPOSIDs: filteredPOS?.map(item => item?.value)
    };
    // #endregion

    // #region DATA MODEL STATES
    const [seasonName, setSeasonName] = useState("");
    const [noosCollection, setNoosCollection] = useState(false);
    const [deliveryWindow, setDeliveryWindow] = useState(null);
    const [endOfLifecycle, setEndOfLifecycle] = useState(null);
    const [appendCalendarMonth, setAppendCalendarMonth] = useState(false);
    const [editSaveButtonEnabled, setEditSaveButtonEnabled] = useState(false);
    const [editingSeasonDefault, setEditingSeasonDefault] = useState(null);
    const [seasonEditSnapshot, setSeasonEditSnapshot] = useState(null);
    // #endregion

    // #region LIST COMPONENT VARS
    const [listComponentIsLoading, setListComponentIsLoading] = useState(false);
    const [listNumRows, setListNumRows] = useState(50);
    const [seasonDefaultsList, setSeasonDefaultsList] = useState([]);
    const listQueryKey = ["MyDefaultSeasonsList", filters];

    const listColumns = [
        {
            name: "Season name",
            selector: row => row?.seasonNameFromPOS ?? "--"
        },
        {
            name: "Season ID",
            selector: row => row?.seasonIdFromPOS || "--"
        },
        {
            name: "NOOS",
            selector: row => (row?.isNOOS === true ? "Yes" : row?.isNOOS === false ? "No" : "--")
        },
        {
            name: "Delivery window",
            selector: row =>
                row?.deliveryWindowStartDate
                    ? formatDefaultDate(row?.deliveryWindowStartDate) +
                      " - " +
                      formatDefaultDate(row?.deliveryWindowEndDate)
                    : "--"
        },
        {
            name: "End of lifecycle",
            selector: row => formatDefaultDate(row?.endOfLifecycleDay)
        },
        {
            name: "Lifecyle (days)",
            selector: row => {
                if (row?.isNOOS === true) return "--";
                if (!row?.deliveryWindowEndDate || !row?.endOfLifecycleDay) return "--";
                const endDate = moment(row.deliveryWindowEndDate);
                const lifecycleDate = moment(row.endOfLifecycleDay);
                if (!endDate.isValid() || !lifecycleDate.isValid()) return "--";
                return Math.abs(endDate.diff(lifecycleDate, "days"));
            }
        },
        {
            name: "Created by",
            selector: row => row?.createdBy || "--"
        },
        {
            name: "Created on",
            selector: row => row?.createdOn || "--"
        },
        {
            name: "Append month",
            selector: row => (row?.appendMonth === true ? "Yes" : row?.appendMonth === false ? "No" : "--")
        },
        {
            name: "Status",
            selector: row => (
                <StatusBadge statusValue={row?.status ?? "--"} colorValue={getColorFromStatus(row?.status)} />
            )
        }
    ];
    // #endregion

    // #region METHODS
    const onRefreshAction = () => {
        AppQueryClient.invalidateQueries(listQueryKey);
    };

    const onFiltersLoaded = data => {
        const defaultRetailBrandValue =
            sessionStorage.getItem("currentRetailBrand") || localStorage.getItem("defaultRetailBrand");

        if (data?.retailBrands?.length) {
            const retailBrands = data?.retailBrands?.map(item => {
                const defaultRetailBrandID = localStorage.getItem("defaultRetailBrand");
                let retailBrandName = item?.name;

                if (item?._id === defaultRetailBrandID) {
                    retailBrandName = `${item?.name} (default)`;
                }

                return {
                    name: retailBrandName,
                    value: item?._id,
                    selected: item?._id === defaultRetailBrandValue
                };
            });

            const defaultRetailBrandObj =
                retailBrands?.find(item => item?.value === defaultRetailBrandValue) || retailBrands?.[0];

            setAllRetailBrandsList(retailBrands);
            setSelectedRetailBrand(defaultRetailBrandObj);
        }

        if (data?.posSystems?.length) {
            const formFields = data?.posSystems?.map(item => ({
                ...item,
                name: item.name,
                value: item._id,
                selected: true
            }));

            setAllPOSList(formFields);
            setFilteredPOS(formFields);
        }

        setRequestsEnabled(true);
    };

    const onRetailBrandChange = selection => {
        setSeasonDefaultsList([]);
        setSelectedRetailBrand(selection);
        const currentRetailBrandID = selection?.value;
        sessionStorage.setItem("currentRetailBrand", currentRetailBrandID || "");
    };

    const onSelectedPOSChanged = (selections, dropdownDataWithSelections) => {
        setSeasonDefaultsList([]);
        setFilteredPOS(selections);
        setAllPOSList(dropdownDataWithSelections);
    };

    const getCurrentSeasonSnapshot = () => ({
        seasonName: seasonName ?? "",
        isNOOS: !!noosCollection,
        deliveryWindowStartDate: noosCollection ? "" : normalizeDateForComparison(deliveryWindow?.[0]),
        deliveryWindowEndDate: noosCollection ? "" : normalizeDateForComparison(deliveryWindow?.[1]),
        endOfLifecycleDay: noosCollection ? "" : normalizeDateForComparison(endOfLifecycle),
        appendMonth: !!appendCalendarMonth
    });

    const seasonFormIsValid = () => {
        if (!seasonName) return false;
        if (noosCollection) return true;

        return !!(deliveryWindow?.[0] && deliveryWindow?.[1] && endOfLifecycle);
    };

    const resetSeasonForm = () => {
        setSeasonName("");
        setDeliveryWindow(null);
        setEndOfLifecycle(null);
        setNoosCollection(false);
        setAppendCalendarMonth(false);
        setEditingSeasonDefault(null);
        setSeasonEditSnapshot(null);
        setEditSaveButtonEnabled(false);
    };

    const handleChange = dates => {
        // set end of lifecycle to null if delivery window is incomplete or end date is higher than lifecycle
        if (
            !dates?.[0] ||
            !dates?.[1] ||
            (dates && dates[1] && endOfLifecycle && dates[1].isAfter(endOfLifecycle, "day"))
        ) {
            setEndOfLifecycle(null);
        }

        setDeliveryWindow(dates);
    };

    const getLifecycleAssistiveText = () => {
        //In 'End of lifecycle' field use the Assistive Text to show the number of calendar days between the last delivery window day and the end of lifecycle day.

        if (!deliveryWindow?.[0] || !deliveryWindow?.[1] || !endOfLifecycle || noosCollection) return "BOTAG ONLY";
        return `Calendar days: ${Math.abs(deliveryWindow[1].diff(endOfLifecycle, "days"))}`;
    };

    const showSeasonDefaultsSyncedPopup = () => {
        const messageHtml =
            pendingSeasonDefaultsCount === 1
                ? `The pending season has been sent to ${syncPOSName}. The POS Season ID is now saved in BOtag and shown in the table.`
                : `The pending seasons have been sent to ${syncPOSName}. The POS Season IDs are now saved in BOtag and shown in the table.`;

        Library.showConfirmationMessage({
            setGenericConfirmationPopup,
            useV2: true,
            headingText: "SUCCESS!",
            headingColor: "var(--text-icons-green-500)",
            title: (
                <DialogWithIcon className="frame-160">
                    <HelpCircle className="help-circle" />
                    <div className="frame-286">
                        <div className="title">Seasons synced</div>
                    </div>
                </DialogWithIcon>
            ),
            messageHtml,
            confirmText: "OK",
            showCancel: false,
            confirmState: "secondary",
            confirmTextColor: "var(--text-icons-green-500)",
            onConfirmClicked: () => {
                setGenericConfirmationPopup(false);
            }
        });
    };

    const onClickEditHandler = row => {
        if (
            row?.seasonIdFromPOS === undefined ||
            row?.seasonIdFromPOS === null ||
            String(row?.seasonIdFromPOS).trim() === ""
        ) {
            Library.showToastMessage({
                type: "warning",
                message: "Unable to edit this season because the POS season ID is missing."
            });
            return;
        }

        const rowSnapshot = getSeasonSnapshotFromRow(row);
        const rowDeliveryWindow =
            !rowSnapshot.isNOOS && row?.deliveryWindowStartDate && row?.deliveryWindowEndDate
                ? [getMomentDate(row.deliveryWindowStartDate), getMomentDate(row.deliveryWindowEndDate)]
                : null;

        setEditingSeasonDefault({
            seasonIdFromPOS: row.seasonIdFromPOS,
            seasonNameFromPOS: row?.seasonNameFromPOS
        });
        setSeasonEditSnapshot(rowSnapshot);
        setSeasonName(rowSnapshot.seasonName);
        setNoosCollection(rowSnapshot.isNOOS);
        setDeliveryWindow(rowDeliveryWindow?.[0] && rowDeliveryWindow?.[1] ? rowDeliveryWindow : null);
        setEndOfLifecycle(rowSnapshot.isNOOS ? null : getMomentDate(row?.endOfLifecycleDay));
        setAppendCalendarMonth(rowSnapshot.appendMonth);
        setEditSaveButtonEnabled(false);
        setExportButtonEnabled(false);
        setHasUnsavedChanges(false);
    };

    // Dormant for this phase: EXPORT stays locked because edits do not sync back to POS.
    const onExportClicked = async () => {
        if (!hasPendingSeasonDefaults) return;

        Library.showConfirmationMessage({
            setGenericConfirmationPopup,
            useV2: true,
            headingText: "JUST CHECKING...",
            headingColor: "var(--text-icons-green-500)",
            title: (
                <DialogWithIcon className="frame-160">
                    <HelpCircle className="help-circle" />
                    <div className="frame-286">
                        <div className="title">Sync seasons?</div>
                    </div>
                </DialogWithIcon>
            ),
            messageHtml: `The pending season${pendingSeasonDefaultsCount === 1 ? "" : "s"} will be sent to ${syncPOSName}.`,
            confirmText: "SYNC",
            cancelText: "CANCEL",
            confirmState: "secondary",
            confirmTextColor: "var(--text-icons-green-500)",
            onConfirmClicked: async () => {
                setGenericConfirmationPopup(false);
                await syncSeasonDefaultsRequest();
            },
            onCancelClicked: () => {
                setGenericConfirmationPopup(false);
            }
        });
    };

    // Dormant for this phase: remove/delete must be handled directly in POS.
    /* const onClickRemoveHandler = async row => {
        const seasonDisplayName = row?.seasonNameFromPOS ?? row?.seasonName;

        if (!getDeleteSeasonDefaultPayload(row)) {
            Library.showToastMessage({
                type: "warning",
                message: "Unable to delete this season because the season name is missing."
            });
            return;
        }

        Library.showConfirmationMessage({
            setGenericConfirmationPopup,
            useV2: true,
            headingText: "ATTENTION!",
            headingColor: "var(--text-icons-orange-500, #f90)",
            title: (
                <DialogWithIcon className="frame-160">
                    <HelpCircle className="help-circle text-orange-500" />
                    <div className="frame-286">
                        <div className="title">Delete season?</div>
                    </div>
                </DialogWithIcon>
            ),
            messageHtml: `Are you sure you want to delete season <strong>${seasonDisplayName}?</strong>`,
            confirmText: "YES",
            cancelText: "CANCEL",
            confirmState: "secondary",
            confirmTextColor: "var(--text-icons-orange-500)",
            onConfirmClicked: async () => {
                setGenericConfirmationPopup(false);

                const success = await deleteSeasonDefaultRequest(row);
                if (success)
                    Library.showToastMessage({
                        message: `Season ${seasonDisplayName} deleted successfully`,
                        type: "success"
                    });
            },
            onCancelClicked: () => {
                setGenericConfirmationPopup(false);
            }
        });
    }; */

    const saveSeasonEditClicked = async () => {
        if (!editingSeasonDefault || !editSaveButtonEnabled) return;

        const success = await updateSeasonDefaultRequest(editingSeasonDefault.seasonIdFromPOS);
        if (!success) return;

        Library.showToastMessage({
            type: "success",
            message: `Season ${seasonName} updated successfully`
        });

        resetSeasonForm();
        setHasUnsavedChanges(false);
    };
    // #endregion

    // #region HELPERS METHODS
    const getMomentDate = date => {
        if (!date || date === "-") return null;

        const strictDate = moment(date, [moment.ISO_8601, "YYYY-MM-DD", "DD/MM/YYYY"], true);
        if (strictDate.isValid()) return strictDate;

        const fallbackDate = moment(date);
        return fallbackDate.isValid() ? fallbackDate : null;
    };

    const normalizeDateForComparison = date => {
        const DATE_COMPARISON_FORMAT = "YYYY-MM-DD";
        return getMomentDate(date)?.format(DATE_COMPARISON_FORMAT) || "";
    };

    const normalizeBoolean = value => {
        if (typeof value === "boolean") return value;
        if (typeof value === "number") return value === 1;
        if (typeof value !== "string") return !!value;

        const normalizedValue = value.trim().toLowerCase();
        if (["true", "yes", "y", "1"].includes(normalizedValue)) return true;
        if (["false", "no", "n", "0", "", "--"].includes(normalizedValue)) return false;

        return !!value;
    };

    const seasonStatusIsPending = status =>
        String(status || "")
            .trim()
            .toLowerCase() === "pending";

    const getPOSNameForSyncPopup = posList => {
        if (posList?.length === 1) return posList?.[0]?.name || "POS";
        if (posList?.length > 1) return "selected POS systems";

        return "POS";
    };

    /* const getDeleteSeasonDefaultPayload = row => {
        const valueIsPresent = value => value !== undefined && value !== null && String(value).trim() !== "";

        if (valueIsPresent(row?.seasonIdFromPOS)) return { seasonIdFromPOS: row.seasonIdFromPOS };

        const seasonName = row?.seasonNameFromPOS ?? row?.seasonName;
        if (!valueIsPresent(seasonName)) return null;

        return { seasonName };
    }; */

    const getSeasonSnapshotFromRow = row => {
        const isNOOS = row?.isNOOS === true;

        return {
            seasonName: row?.seasonNameFromPOS ?? row?.seasonName ?? "",
            isNOOS,
            deliveryWindowStartDate: isNOOS ? "" : normalizeDateForComparison(row?.deliveryWindowStartDate),
            deliveryWindowEndDate: isNOOS ? "" : normalizeDateForComparison(row?.deliveryWindowEndDate),
            endOfLifecycleDay: isNOOS ? "" : normalizeDateForComparison(row?.endOfLifecycleDay),
            appendMonth: normalizeBoolean(row?.appendMonth)
        };
    };

    const seasonSnapshotsAreEqual = (snapshotA, snapshotB) => {
        if (!snapshotA || !snapshotB) return false;

        return Object.keys(snapshotA).every(key => snapshotA[key] === snapshotB[key]);
    };
    // #endregion

    // #region REQUESTS
    /* const createSeasonDefaultRequest = async () => {
        const request = {
            retailBrandID: selectedRetailBrand?.value,
            seasonName,
            isNOOS: noosCollection,
            deliveryWindowStartDate: noosCollection ? "" : deliveryWindow?.[0] || "",
            deliveryWindowEndDate: noosCollection ? "" : deliveryWindow?.[1] || "",
            endOfLifecycleDay: noosCollection ? "" : endOfLifecycle || "",
            appendMonth: appendCalendarMonth
        };

        const res = await Library.makeApiRequest({
            endpoint: "createSeasonDefault",
            method: "POST",
            data: request,
            setLoader: setIsSaving
        });

        if (res?.data?.status !== 200) return;

        onRefreshAction();
        resetSeasonForm();
    }; */

    const syncSeasonDefaultsRequest = async () => {
        const request = {
            retailBrandID: selectedRetailBrand?.value
        };

        const res = await Library.makeApiRequest({
            endpoint: "syncSeasonDefaults",
            method: "POST",
            data: request,
            setLoader: setIsSaving
        });

        if (res?.data?.status !== 200) return;

        onRefreshAction();
        showSeasonDefaultsSyncedPopup();
    };

    const updateSeasonDefaultRequest = async seasonIdFromPOS => {
        const request = {
            retailBrandID: selectedRetailBrand?.value,
            seasonName,
            isNOOS: noosCollection,
            deliveryWindowStartDate: noosCollection ? "" : deliveryWindow?.[0] || "",
            deliveryWindowEndDate: noosCollection ? "" : deliveryWindow?.[1] || "",
            endOfLifecycleDay: noosCollection ? "" : endOfLifecycle || "",
            appendMonth: appendCalendarMonth,
            seasonIdFromPOS
        };

        const res = await Library.makeApiRequest({
            endpoint: "updateSeasonDefault",
            method: "POST",
            data: request,
            setLoader: setIsSaving
        });

        if (res?.data?.status !== 200) return false;

        onRefreshAction();
        return true;
    };

    /* const deleteSeasonDefaultRequest = async row => {
        const seasonPayload = getDeleteSeasonDefaultPayload(row);
        if (!seasonPayload) return false;

        const request = {
            retailBrandID: selectedRetailBrand?.value,
            ...seasonPayload
        };

        const res = await Library.makeApiRequest({
            endpoint: "deleteSeasonDefault",
            method: "POST",
            data: request,
            setLoader: setIsSaving
        });

        if (res?.data?.status !== 200) return false;

        onRefreshAction();
        return true;
    }; */
    // #endregion

    // #region COMPUTED VARS
    const isEditingSeason = !!editingSeasonDefault;
    const currentSeasonSnapshot = getCurrentSeasonSnapshot();
    const editHasChanges =
        isEditingSeason && seasonEditSnapshot && !seasonSnapshotsAreEqual(seasonEditSnapshot, currentSeasonSnapshot);
    const seasonFormLocked = !isEditingSeason;
    const isDeliveryWindowFilled = !!(deliveryWindow?.[0] && deliveryWindow?.[1]);
    const endOfLifecycleLocked = seasonFormLocked || noosCollection || !isDeliveryWindowFilled;
    const hasPendingSeasonDefaults = seasonDefaultsList?.some(season => seasonStatusIsPending(season?.status));
    const pendingSeasonDefaultsCount = seasonDefaultsList?.filter(season =>
        seasonStatusIsPending(season?.status)
    )?.length;
    const syncPOSName = getPOSNameForSyncPopup(filteredPOS);
    // #endregion

    // #region EFFECTS
    onExportClickedRef.current = onExportClicked; // Calls onExportClicked always with updated state variables

    useEffect(() => {
        setupHeaders({
            props,
            onRefreshAction,
            allRetailBrandsList,
            onRetailBrandChange,
            selectedRetailBrand,
            allPOSList,
            onSelectedPOSChanged,
            onFiltersLoaded,
            exportButtonEnabled,
            onExportClicked: () => onExportClickedRef.current?.()
        });
    }, [allRetailBrandsList, selectedRetailBrand, allPOSList, exportButtonEnabled]); //Setup Headers Effect

    useEffect(() => {
        setEditSaveButtonEnabled(isEditingSeason && seasonFormIsValid() && !!editHasChanges);
        setExportButtonEnabled(false);
        setHasUnsavedChanges(isEditingSeason && !!editHasChanges);
    }, [
        seasonName,
        deliveryWindow,
        endOfLifecycle,
        noosCollection,
        appendCalendarMonth,
        editingSeasonDefault,
        seasonEditSnapshot
    ]); //FormIsValid Change Effect
    // #endregion

    // #region RENDER
    return (
        <StyledPage className="my-defaults-seasons-page">
            <FullScreenOverlay show={isSaving}>
                <DialogBoxComponent
                    headingColor="var(--text-icons-green-500)"
                    heading={isEditingSeason ? "UPDATING..." : "SAVING..."}
                    showCancel={false}
                    showConfirm={false}
                    loading={isSaving}
                />
            </FullScreenOverlay>

            <FormContainer title="DEFAULT SEASON DETAILS" showDefaultRightLegends={true}>
                <div className="add-season-row">
                    <div className="add-season-fields">
                        <FormField
                            type="text"
                            title="Season name"
                            placeholder="Insert season name"
                            requiredType="disabledSimple"
                            varName="seasonName"
                            tooltip="Season name"
                            value={seasonName}
                        />
                        <div className="append-month-control">
                            <CheckboxVector
                                disabled={seasonFormLocked}
                                isChecked={noosCollection}
                                onCheckboxClick={() => setNoosCollection(prev => !prev)}
                            />
                            <span className={`append-month-label ${seasonFormLocked ? "text-inactive" : ""}`}>
                                This season is a NOOS collection
                            </span>
                            <TooltipSimple
                                header="NOOS means Never Out Of Stock."
                                title={
                                    <>
                                        Check this box when this season contains NOOS items: products that are
                                        continuously available, such as basics or recurring items, instead of products
                                        made only for one seasonal collection.
                                        <br />
                                        <br />
                                        When this box is checked, Delivery Window and End of Lifecycle are locked
                                        because the season is treated as ongoing.
                                        <br />
                                        <br />
                                        If the season contains both NOOS and seasonal items, you can override the NOOS
                                        setting per item from Products &gt; LIVE &gt; Details.
                                        <br />
                                        <br />
                                        BOtag treats NOOS items with a 60-day lifecycle for reorder logic.
                                        <br />
                                        <br />
                                        This setting is saved in BOtag and is not sent to Front Systems.
                                    </>
                                }
                                width="340px">
                                <InfoCircle
                                    className={`append-month-info ${seasonFormLocked ? "text-inactive" : ""}`}
                                />
                            </TooltipSimple>
                        </div>

                        <FormFieldDateRangePicker
                            className="w-100 delivery-window-field"
                            value={deliveryWindow}
                            disabled={seasonFormLocked || noosCollection}
                            onChange={handleChange}
                            title="Delivery window"
                            placeholder="Insert delivery window"
                            requiredType={seasonFormLocked || noosCollection ? "locked" : "mandatory"}
                            varName="deliveryWindow"
                            tooltip="Delivery window"
                            assistiveText="BOTAG ONLY"
                            assistiveTextColorOverride="var(--text-icons-green-500)"
                            displayValue={
                                !deliveryWindow?.[0] || !deliveryWindow?.[1] || noosCollection
                                    ? ""
                                    : `${formatDefaultDate(deliveryWindow[0])} - ${formatDefaultDate(deliveryWindow[1])}`
                            }
                            onClearAction={() => {
                                setDeliveryWindow([null, null]);
                                setEndOfLifecycle(null);
                            }}
                        />

                        <FormFieldDatePicker
                            className="end-of-lifecycle-field"
                            value={endOfLifecycle}
                            disabled={endOfLifecycleLocked}
                            onChange={date => setEndOfLifecycle(date)}
                            disableBefore={deliveryWindow?.[1] || null}
                            title="End of lifecycle"
                            placeholder="Insert end of lifecycle"
                            requiredType={endOfLifecycleLocked ? "locked" : "mandatory"}
                            varName="endOfLifecycle"
                            tooltip="End of lifecycle"
                            assistiveText={getLifecycleAssistiveText()}
                            assistiveTextColorOverride="var(--text-icons-green-500)"
                            displayValue={!endOfLifecycle || noosCollection ? "" : formatDefaultDate(endOfLifecycle)}
                            onClearAction={() => setEndOfLifecycle(null)}
                        />
                    </div>

                    <div className="append-month-control">
                        <CheckboxVector
                            disabled={seasonFormLocked}
                            isChecked={appendCalendarMonth}
                            onCheckboxClick={() => setAppendCalendarMonth(prev => !prev)}
                        />
                        <span className={`append-month-label ${seasonFormLocked ? "text-inactive" : ""}`}>
                            Append calendar month to season
                        </span>
                        <TooltipSimple title="Adds the calendar month suffix to the season name.">
                            <InfoCircle className={`append-month-info ${seasonFormLocked ? "text-inactive" : ""}`} />
                        </TooltipSimple>
                    </div>

                    <BtnLargeSolid
                        className="add-season-button"
                        text={isEditingSeason ? "UPDATE" : "ADD"}
                        disabled={!editSaveButtonEnabled}
                        normalColor="var(--Buttons-All-Platforms-Gray-500)"
                        hoverColor="var(--Buttons-All-Platforms-Gray-500)"
                        hoverBorderColor="var(--text-icons-orange-500)"
                        disabledColor="var(--buttons-all-platforms-gray-500, #10141b)"
                        textStyle={{
                            fontSize: "11px",
                            textTransform: "uppercase"
                        }}
                        onClick={saveSeasonEditClicked}
                    />
                </div>
            </FormContainer>

            <ContainerLoader
                isLoading={!isLoadingApp && listComponentIsLoading}
                className="w-100"
                isLoadingOverlay={true}
                addBlurFilters={false}>
                <ListComponent
                    className="w-100"
                    tableClassName="expandedRowsCentered"
                    title="DEFAULT SEASONS - as in pos"
                    columns={listColumns}
                    api={{
                        endpoint: "getSeasonDefaults",
                        formData: {
                            retailBrandID: selectedRetailBrand?.value || "",
                            limit: listNumRows,
                            ...filters
                        }
                        //onSuccess: mapListData
                    }}
                    selectableRows={true}
                    showLoader={false}
                    onListLoading={loading => setListComponentIsLoading(loading)}
                    setData={setSeasonDefaultsList}
                    pageLimit={listNumRows}
                    currentPage={1}
                    queryKey={listQueryKey}
                    requestEnabled={requestsEnabled}
                    onChangeRowsPerPage={rowsPerPage => setListNumRows(rowsPerPage)}
                    actions={{
                        includeActions: true,
                        editIsVisible: true,
                        onClickEdit: onClickEditHandler,
                        editIsDisabled: row =>
                            row?.seasonIdFromPOS === undefined ||
                            row?.seasonIdFromPOS === null ||
                            String(row?.seasonIdFromPOS).trim() === "",
                        removeIsVisible: true,
                        suspendIsVisible: false,
                        actionColumnWidth: "100px",
                        actionColumnTitle: "Actions"
                    }}
                />
            </ContainerLoader>
        </StyledPage>
    );
    // #endregion
}

// #region HEADERS
function setupHeaders({
    props,
    onRefreshAction,
    allRetailBrandsList,
    onRetailBrandChange,
    selectedRetailBrand,
    allPOSList,
    onSelectedPOSChanged,
    onFiltersLoaded,
    onExportClicked,
    exportButtonEnabled
}) {
    props.setControlBarLeftBtns([
        {
            customSearch: <CustomSearch enabled={false} placeholder="Search" />
        }
    ]);

    props.setControlBarCenterBtns([
        { to: "/products", active: false, disabled: false, title: "LIVE" },
        { to: "/products/my-defaults/seasons", active: true, disabled: false, title: "MY DEFAULTS" }
    ]);

    props.setControlBarRightBtns([
        {
            to: "/products/my-defaults/seasons",
            active: true,
            disabled: false,
            title: "Seasons",
            icon: "snowflake-02"
        },
        {
            to: "/products/my-defaults/genders",
            active: false,
            disabled: false,
            title: "Genders",
            icon: "link-05"
        },
        {
            to: "/products/my-defaults/colors",
            active: false,
            disabled: false,
            title: "Colors",
            icon: "palette"
        },
        {
            to: "/products/my-defaults/categories",
            active: false,
            disabled: false,
            title: "Categ",
            icon: "grid-01"
        },
        {
            to: "/products/my-defaults/sizes",
            active: false,
            disabled: false,
            title: "Sizes",
            icon: "ruler"
        }
    ]);

    props.setFilterHeaderProperties({
        customContentHeader: (
            <MainWindowHeader
                key={window.location.pathname}
                visible={true}
                layoutTypeOptions={{
                    useLayoutButton: {
                        useInsteadOfGridList: true,
                        text: "EXPORT",
                        enabled: exportButtonEnabled,
                        onClick: onExportClicked
                    },
                    gridEnabled: false,
                    listEnabled: false
                }}
                search={{
                    visible: false
                }}
                onRefreshClick={onRefreshAction}
                firstOrganize={{
                    visible: true,
                    dropdownData: allRetailBrandsList,
                    onDropdownChange: onRetailBrandChange,
                    disabled: false,
                    type: "singleSelect",
                    selectedValue: selectedRetailBrand?.name
                }}
                secondOrganize={{
                    visible: true,
                    dropdownData: allPOSList,
                    onDropdownChange: onSelectedPOSChanged,
                    disabled: false,
                    type: "multiSelect",
                    multidropdownOptions: {
                        selectAllText: "Select all POS",
                        labelAllSelected: "All POS",
                        labelNoneSelected: "--",
                        allItems: allPOSList
                    }
                }}
                pageFilters={{
                    useFilters: true,
                    filters: {
                        useRetailBrands: true,
                        usePOSSystems: true
                    },
                    onFiltersLoaded: onFiltersLoaded
                }}
                settingsButton={{
                    visible: true,
                    enabled: false
                }}
            />
        ),
        visible: false,
        gridEnabled: false,
        listEnabled: false,
        onRefreshAction
    });
}
// #endregion
